import { useDispatch, useSelector } from "react-redux";
import { NavLink, Outlet, useNavigate, useSearchParams } from "react-router-dom";
import classNames from "classnames";
import { actionClearStorage, fetchMilestones, fetchUserInfo } from "../../lib/actions";
import { useEffect, useState } from "react";
import { AlertModal, LocationErrorModal, ConfirmDialog } from "./Helper";
import Header from "./Header";
import Icon from "../ds/Icon";
import { LogoMark } from "../ds/LogoMark";
import { IssueModal } from "../perf/IssueBlock";

const NavItem = ({ to, onClick, children }) => (
    <NavLink
        to={to}
        onClick={onClick}
        className={({ isActive }) => classNames("app-nav__item", { "is-active": isActive })}
    >
        {children}
    </NavLink>
);

const Layout = () => {
    const [sp] = useSearchParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [confirmLogout, setConfirmLogout] = useState(false);
    const { userInfo, appConfig, uiState } = useSelector(st => ({
        userInfo: st.userInfo,
        uiState: st.uiState,
        appConfig: st.appConfig
    }));

    useEffect(() => {
        if (userInfo) {
            return;
        }
        dispatch(fetchUserInfo(false)).then(resp => {
            const data = resp.data;
            dispatch({ type: 'SET_APP_CONF', data: data.config });
        }).catch(ex => {
            console.error(ex);
            // not authenticated → clear the dead session and return to the public
            // landing (clearing prevents a landing↔dashboard redirect loop).
            actionClearStorage();
            document.location.href = "/";
        });
    }, [1]);

    useEffect(() => {
        if (!userInfo) {
            return;
        }
        dispatch(fetchMilestones());
    }, [userInfo?.email, dispatch]);

    const doLogout = () => {
        actionClearStorage();
        document.location.href = "/";
    };

    const closeDrawer = () => dispatch({ type: 'close-sidebar' });

    const query = new URLSearchParams(sp.toString());
    const allowed = ['start', 'end'];
    for (const [key] of query.entries()) {
        if (!allowed.some(item => item === key)) {
            query.delete(key);
        }
    }
    const qs = query.toString();

    if (!userInfo) {
        return (
            <div className="ds-scope ds-boot" role="status" aria-live="polite">
                <div className="ds-boot__mark">Focuz<span>.</span></div>
                <div className="ds-boot__bar" />
                <div className="ds-boot__msg">Preparing your workspace…</div>
            </div>
        );
    }

    const routeSections = appConfig.sections.filter(item => item.enabled === 'TRUE' && item.module === 'routes');
    // Group Leaves / Contributions / Utilisation under a dedicated "Activity" nav
    // section (rendered as sub-tabs under /activity/*).
    const ACTIVITY_VALUES = ['leaves', 'contrib', 'utilisation'];
    const activitySections = routeSections.filter(i => ACTIVITY_VALUES.includes(i.value.toLowerCase()));
    const workspaceSections = routeSections.filter(i => !ACTIVITY_VALUES.includes(i.value.toLowerCase()));

    return (
        <div className={classNames("ds-scope app-shell", { "is-drawer-open": uiState.sidebarOpen })}>
            <aside className="app-sidebar" aria-label="Primary">
                <div className="app-sidebar__brand">
                    <LogoMark className="app-sidebar__logo" size={30} />
                    <span className="app-sidebar__wordmark">Focuz</span>
                    <button
                        type="button"
                        onClick={closeDrawer}
                        className="ds-btn is-ghost is-icon is-sm app-sidebar__close"
                        aria-label="Close menu"
                    >
                        <Icon name="close" />
                    </button>
                </div>

                <nav className="app-sidebar__nav">
                    {userInfo.role === 'Admin' && (
                        <>
                            <div className="app-nav__section">Overview</div>
                            <NavItem to={`/admin-dashboard/?${qs}`} onClick={closeDrawer}>Admin Dashboard</NavItem>
                        </>
                    )}

                    <div className="app-nav__section">Workspace</div>
                    {workspaceSections.map(item => (
                        <NavItem key={item.value} to={`/${item.value.toLowerCase()}?${qs}`} onClick={closeDrawer}>
                            {item.name}
                        </NavItem>
                    ))}

                    {activitySections.length > 0 && (
                        <NavItem to={`/activity?${qs}`} onClick={closeDrawer}>Activity</NavItem>
                    )}

                    <div className="app-nav__section">More</div>
                    {userInfo.role === 'Admin' && (
                        <NavItem to={`/settings?${qs}`} onClick={closeDrawer}>Settings</NavItem>
                    )}
                    <NavItem to={`/docs?${qs}`} onClick={closeDrawer}>Download &amp; FAQ</NavItem>
                </nav>

                <div className="app-sidebar__foot">
                    <div className="app-user">
                        {userInfo.imageUrl
                            ? <img className="app-user__avatar" src={userInfo.imageUrl} alt="" />
                            : <span className="app-user__avatar is-initials">{(userInfo.name || '?').trim().charAt(0).toUpperCase()}</span>}
                        <span className="app-user__name" title={userInfo.name}>{userInfo.name}</span>
                        <button
                            type="button"
                            onClick={() => setConfirmLogout(true)}
                            className="ds-btn is-ghost is-icon is-sm app-user__logout"
                            aria-label="Log out"
                            title="Log out"
                        >
                            <Icon name="logout" />
                        </button>
                    </div>
                    <a className="app-sidebar__credit" href="https://zcalo.ai" rel="noreferrer" target="_blank" title="zcalo.ai">
                        Focuz
                    </a>
                </div>
            </aside>

            <div className="app-scrim" onClick={closeDrawer} aria-hidden="true" />

            <Header />

            <main className="app-main">
                <div className="app-main__inner">
                    <Outlet />
                </div>
            </main>

            <AlertModal />
            <LocationErrorModal />
            <IssueModal />
            <ConfirmDialog
                open={confirmLogout}
                title="Log out?"
                message="You’ll need to sign in again to access your dashboard."
                confirmLabel="Log out"
                danger
                onConfirm={doLogout}
                onCancel={() => setConfirmLogout(false)}
            />
        </div>
    );
};

export default Layout;
